"use client";

import { useActionState, useEffect, useState } from "react";
import { useFormStatus } from "react-dom";
import { toast } from "sonner";

import { deleteProduct } from "./actions";

type DeleteProductButtonProps = {
  productId: string;
  productName: string;
};

type ActionState = {
  ok: boolean;
  message: string;
};

const INITIAL_STATE: ActionState = {
  ok: false,
  message: "",
};

function ConfirmDeleteButton() {
  const { pending } = useFormStatus();

  return (
    <button
      type="submit"
      disabled={pending}
      className="inline-flex min-h-10 items-center justify-center rounded-full bg-[#c95a84] px-4 py-2 text-sm font-medium text-white shadow-[0_10px_24px_rgba(201,90,132,0.22)] transition hover:bg-[#b84a74] disabled:cursor-not-allowed disabled:opacity-60"
    >
      {pending ? "Suppression..." : "Supprimer l'article"}
    </button>
  );
}

export default function DeleteProductButton({
  productId,
  productName,
}: DeleteProductButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [state, formAction] = useActionState(deleteProduct, INITIAL_STATE);

  useEffect(() => {
    if (!state.message) {
      return;
    }

    if (state.ok) {
      toast.success(state.message);
      setIsOpen(false);
      return;
    }

    toast.error(state.message);
  }, [state]);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex min-h-9 items-center justify-center rounded-full border border-[#f0bfd3] bg-[#fff7fb] px-3.5 py-1.5 text-xs font-medium text-[#b24b76] transition hover:border-[#e3a9c5] hover:bg-[#fff0f6]"
      >
        Supprimer
      </button>

      {isOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="Fermer la fenêtre de suppression"
            onClick={() => setIsOpen(false)}
            className="absolute inset-0 bg-[#2a1821]/40 backdrop-blur-[3px]"
          />

          <div className="relative z-10 w-full max-w-lg rounded-[1.75rem] border border-[#f0dfe7] bg-[linear-gradient(135deg,#fffafb_0%,#fffdfb_100%)] p-5 shadow-[0_24px_80px_rgba(36,22,29,0.14)] sm:p-6">
            <div className="space-y-2.5">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.22em] text-[#c95a84]">
                Suppression définitive
              </p>
              <h3 className="text-[1.8rem] font-semibold leading-[1] tracking-[-0.04em] text-[#24161d]">
                Supprimer cet article ?
              </h3>
              <p className="text-[13px] leading-6 text-[#6f5a63] sm:text-sm">
                Tu es sur le point de supprimer <span className="font-medium text-[#24161d]">{productName}</span>.
                Cette action est irréversible avec le système actuel.
              </p>
            </div>

            <form action={formAction} className="mt-6 space-y-5">
              <input type="hidden" name="productId" value={productId} />

              <div className="rounded-[1.1rem] border border-[#f3d2df] bg-[#fff4f8] px-4 py-3 text-[13px] leading-6 text-[#9a4c6d]">
                La fiche produit, ses images liées en base et son accès depuis
                l&apos;admin seront supprimés.
              </div>

              <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="inline-flex min-h-10 items-center justify-center rounded-full border border-[#efc7d9] bg-white px-4 py-2 text-sm font-medium text-[#6f324e] transition hover:border-[#e3a9c5] hover:bg-[#fff8fb]"
                >
                  Annuler
                </button>
                <ConfirmDeleteButton />
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}